<template>
  <Teleport :to="props.teleportTarget">
    <Transition name="book-share">
      <!-- 面板内点击拦截冒泡，避免触发阅读器翻页 -->
      <section
        v-if="props.visible"
        ref="panelRef"
        class="book-share-panel"
        role="dialog"
        aria-labelledby="book-share-title"
        tabindex="-1"
        :style="panelStyle"
        @click.stop
        @pointerdown.stop
        @pointerup.stop
        @touchstart.stop.passive
        @touchend.stop
      >
        <header class="book-share-header">
          <h2 id="book-share-title" class="book-share-title">{{ shareTitle }}</h2>
          <button
            class="book-share-close"
            type="button"
            aria-label="关闭分享面板"
            @click="emit('close')"
          >
            <SvgIcon name="book-share-close" :size="24" />
          </button>
        </header>

        <div class="book-share-link-row">
          <input
            ref="linkRef"
            class="book-share-link"
            type="text"
            :value="shareUrl"
            :title="shareUrl"
            readonly
            @click="selectShareUrl"
            @focus="selectShareUrl"
          />
          <button class="book-share-copy" type="button" @click="copyShareUrl">
            <span aria-live="polite">{{ copied ? '已复制' : '复制' }}</span>
          </button>
        </div>

        <p v-if="copyError" class="book-share-error" role="alert">
          复制失败，请长按或手动选中链接复制
        </p>

        <div class="book-share-qr-section">
          <div class="book-share-section-label">扫一扫二维码预览</div>
          <div class="book-share-qr-card">
            <img
              v-if="shareQrUrl"
              class="book-share-qr"
              :src="shareQrUrl.value"
              alt="绘本预览二维码"
            />
            <span v-else class="book-share-qr-empty">二维码生成中…</span>
          </div>
        </div>
      </section>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import { useShareQrcode } from '@/hooks/useShareQrcode';
  import { useProjectStore } from '@/store/project';
  import { storeToRefs } from 'pinia';

  const props = withDefaults(
    defineProps<{
      visible: boolean;
      title: string;
      anchorRect?: DOMRect | null;
      teleportTarget?: string | HTMLElement;
    }>(),
    {
      teleportTarget: 'body'
    }
  );

  const emit = defineEmits<{
    close: [];
  }>();

  const PANEL_MARGIN = 16;
  const ANCHOR_GAP = 12;
  const MOBILE_BREAKPOINT = 480;

  const { shareUrl } = storeToRefs(useProjectStore());
  const shareQrUrl = computed(() => {
    return useShareQrcode(shareUrl.value).dataUrl;
  });
  const copied = ref(false);
  const copyError = ref(false);
  const panelRef = ref<HTMLElement | null>(null);
  const linkRef = ref<HTMLInputElement | null>(null);
  const panelStyle = ref<{ top?: string; left?: string }>({});
  const shareTitle = computed(() => `分享绘本-《${props.title || '未命名绘本'}》`);
  let copiedTimer: number | undefined;
  let copyErrorTimer: number | undefined;
  // 每次面板关闭都自增，使关闭前发起的复制结果在回来时被丢弃，避免跨会话回写状态
  let copySession = 0;
  let previousActiveElement: HTMLElement | null = null;

  // 根据分享按钮位置与右侧剩余空间动态定位面板，尽量贴在按钮上方；窄屏交给 CSS 的底部面板样式
  function updatePanelPosition() {
    const panel = panelRef.value;
    const anchor = props.anchorRect;
    if (!panel || !anchor || window.innerWidth <= MOBILE_BREAKPOINT) {
      panelStyle.value = {};
      return;
    }

    const panelWidth = panel.offsetWidth;
    const panelHeight = panel.offsetHeight;
    const maxLeft = Math.max(PANEL_MARGIN, window.innerWidth - PANEL_MARGIN - panelWidth);
    const left = Math.min(Math.max(anchor.right - panelWidth, PANEL_MARGIN), maxLeft);

    let top = anchor.top - panelHeight - ANCHOR_GAP;
    if (top < PANEL_MARGIN) top = anchor.bottom + ANCHOR_GAP;
    const maxTop = Math.max(PANEL_MARGIN, window.innerHeight - PANEL_MARGIN - panelHeight);
    top = Math.min(Math.max(top, PANEL_MARGIN), maxTop);

    panelStyle.value = { top: `${top}px`, left: `${left}px` };
  }

  function resetCopiedState() {
    copied.value = false;
    if (copiedTimer !== undefined) {
      window.clearTimeout(copiedTimer);
      copiedTimer = undefined;
    }
  }

  function resetCopyError() {
    copyError.value = false;
    if (copyErrorTimer !== undefined) {
      window.clearTimeout(copyErrorTimer);
      copyErrorTimer = undefined;
    }
  }

  async function copyShareUrl() {
    const session = copySession;

    if (!navigator.clipboard) {
      resetCopiedState();
      copyError.value = true;
      copyErrorTimer = window.setTimeout(resetCopyError, 2400);
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl.value);
      if (session !== copySession || !props.visible) return;

      resetCopyError();
      resetCopiedState();
      copied.value = true;
      copiedTimer = window.setTimeout(resetCopiedState, 1600);
    } catch {
      if (session !== copySession || !props.visible) return;

      resetCopiedState();
      copyError.value = true;
      copyErrorTimer = window.setTimeout(resetCopyError, 2400);
    }
  }

  // 点击/聚焦时整体选中，方便复制失败时手动复制完整链接；input 原生支持选区超出可视区域时自动滚动
  function selectShareUrl() {
    linkRef.value?.select();
  }

  function getFocusableElements(): HTMLElement[] {
    if (!panelRef.value) return [];

    return Array.from(
      panelRef.value.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled'));
  }

  function handleKeydown(event: KeyboardEvent) {
    if (props.visible && event.key === 'Escape') emit('close');
  }

  function handleDocumentClick(event: MouseEvent) {
    if (!props.visible) return;
    if (panelRef.value?.contains(event.target as Node)) return;

    emit('close');
  }

  function addOutsideClickListener() {
    document.addEventListener('click', handleDocumentClick, true);
  }

  function removeOutsideClickListener() {
    document.removeEventListener('click', handleDocumentClick, true);
  }

  watch(
    () => props.visible,
    (visible) => {
      if (visible) {
        previousActiveElement = document.activeElement as HTMLElement | null;
        nextTick(() => {
          updatePanelPosition();
          const [first] = getFocusableElements();
          (first ?? panelRef.value)?.focus();
          addOutsideClickListener();
        });
        window.addEventListener('resize', updatePanelPosition);
        return;
      }

      window.removeEventListener('resize', updatePanelPosition);
      removeOutsideClickListener();
      copySession += 1;
      resetCopiedState();
      resetCopyError();
      previousActiveElement?.focus();
      previousActiveElement = null;
    }
  );

  onMounted(() => document.addEventListener('keydown', handleKeydown));
  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
    removeOutsideClickListener();
    window.removeEventListener('resize', updatePanelPosition);
    resetCopiedState();
    resetCopyError();
  });
</script>

<style scoped>
  .book-share-panel {
    position: fixed;
    right: 24px;
    bottom: 96px;
    z-index: 2100;
    width: 401px;
    height: 360px;
    padding: 24px;
    color: #333940;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
    box-sizing: border-box;
    overflow: hidden;
  }

  .book-share-header,
  .book-share-link-row {
    display: flex;
    align-items: center;
  }

  .book-share-header {
    height: 24px;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 14px;
  }

  .book-share-title {
    min-width: 0;
    margin: 0;
    overflow: hidden;
    color: #333940;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .book-share-close,
  .book-share-copy {
    border: 0;
    padding: 0;
    cursor: pointer;
  }

  .book-share-close {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border-radius: 6px;
    transition: background-color 160ms ease;
  }

  .book-share-close:hover {
    background: #f4f2f8;
  }

  .book-share-close:active {
    background: #ebe7f3;
  }

  .book-share-link-row {
    width: 100%;
    height: 40px;
    gap: 8px;
    margin-bottom: 14px;
  }

  .book-share-link {
    min-width: 0;
    height: 40px;
    flex: 1;
    padding: 0 12px;
    color: #999;
    background: #fbf9ff;
    border: 0;
    border-radius: 6px;
    outline: none;
    font-family: inherit;
    font-size: 14px;
    line-height: 22px;
    box-sizing: border-box;
    cursor: text;
  }

  .book-share-link:focus-visible {
    outline: 2px solid #6033e1;
    outline-offset: 2px;
  }

  .book-share-copy {
    width: 68px;
    height: 40px;
    flex: 0 0 68px;
    color: #fff;
    background: #6033e1;
    border-radius: 6px;
    font-size: 14px;
    line-height: 22px;
    transition:
      background-color 160ms ease,
      transform 160ms ease;
  }

  .book-share-copy:hover {
    background: #5126cf;
  }

  .book-share-copy:active {
    background: #4720bb;
    transform: scale(0.97);
  }

  .book-share-error {
    margin: -6px 0 14px;
    color: #e3483a;
    font-size: 12px;
    line-height: 18px;
  }

  .book-share-qr-section {
    width: 100%;
    height: 220px;
  }

  .book-share-section-label {
    height: 22px;
    margin-bottom: 8px;
    color: #333940;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
  }

  .book-share-qr-card {
    width: 100%;
    height: 190px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: #fbf9ff;
    border-radius: 8px;
    box-sizing: border-box;
  }

  .book-share-qr {
    display: block;
    width: 150px;
    height: 150px;
    max-width: 100%;
    border: 1px solid #eff0f7;
    border-radius: 6px;
    object-fit: contain;
    box-sizing: border-box;
  }

  .book-share-qr-empty {
    color: #999;
    font-size: 12px;
    line-height: 18px;
  }

  .book-share-close:focus-visible,
  .book-share-copy:focus-visible {
    outline: 2px solid #6033e1;
    outline-offset: 2px;
  }

  /* 面板自身即 Transition 根元素，进退场直接作用其上：渐隐渐显 + 轻微缩放 */
  .book-share-enter-active,
  .book-share-leave-active {
    transition:
      opacity 200ms ease,
      transform 200ms ease;
  }

  .book-share-enter-from,
  .book-share-leave-to {
    opacity: 0;
    transform: scale(0.96);
  }

  @media (max-width: 480px) {
    .book-share-panel {
      right: 16px;
      bottom: 16px;
      left: 16px;
      width: auto;
      height: auto;
      max-height: calc(100vh - 32px);
      overflow-y: auto;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .book-share-enter-active,
    .book-share-leave-active,
    .book-share-close,
    .book-share-copy {
      transition: none;
    }
  }
</style>
